import React, { useCallback, type FC } from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { USER_ROLES } from '@/constants/user-roles';
import { toTitleCase } from '@/utils/to-title-case';
import { truncateString } from '@/utils/truncate-string';
import { EmailOutlined, LockOutlined } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Megaphone as MegaphoneIcon,
  Paperclip as PaperclipIcon,
} from '@phosphor-icons/react/dist/ssr';
import { Phone01 } from '@untitled-ui/icons-react';
import axios from 'axios';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';

import type { Announcement } from '@/types/announcement';
import { config } from '@/config';
import { paths } from '@/paths';
import AnnouncementAttachmentsDialog from '@/pages/announcements/dialogs/announcement-attachments-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';

interface AnnouncementCardProps {
  data: Announcement;
  excludeLink?: boolean;
  onRefresh?: () => void;
}

const AnnouncementCard: FC<AnnouncementCardProps> = ({ data, excludeLink, onRefresh }) => {
  const { user } = useUser();
  const isScheduled = data.scheduled_at !== null;
  const isFuture = isScheduled && dayjs(data.scheduled_at).isAfter(dayjs());

  const handleCancel = useCallback(async () => {
    try {
      axios.defaults.withCredentials = true;
      await axios.get(`${config.site.api}/sanctum/csrf-cookie`);

      const response = await axios.patch(
        `${config.site.api}/api/v1/announcements/${data.id}`,
        { status: 'cancelled' },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (response.data.error) {
        console.error(response.data.message);
        return;
      }

      console.log('Announcement Cancelled', response.data);
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to cancel announcement');
      console.error(error);
    }
  }, [data.id, onRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const [attachmentsOpen, setAttachmentsOpen] = React.useState(false);

  const cardContent = (
    <CardContent sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Avatar sx={{ backgroundColor: 'primary.main', width: 40, height: 40 }}>
          <MegaphoneIcon size={20} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }} variant="h6">
            {data.subject ?? 'No Subject'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarIcon color="text.secondary" size={16} />
            <Typography color="text.secondary" variant="caption">
              {data.created_at ? dayjs(data.created_at).format('MMM DD, YYYY') : 'N/A'}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              •
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {data.created_at ? dayjs(data.created_at).format('hh:mm A') : 'N/A'}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_OWNER,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.COMMUNITY_MEMBER]}
          >
            <>
              {data.board_members_only ? <Tooltip arrow title="Board Only">
                  <Chip
                    color="primary"
                    icon={<LockOutlined sx={{ fontSize: '1rem' }} />}
                    label="Board Only"
                    size="small"
                    variant="outlined"
                  />
                </Tooltip> : null}
              {isScheduled && isFuture ? <Button
                  color="error"
                  onClick={handleCancel}
                  size="small"
                  sx={{ minWidth: 'auto', px: 1 }}
                  variant="outlined"
                >
                  Cancel
                </Button> : null}
              <Chip
                color={getStatusColor(data.status as string)}
                label={toTitleCase(data.status as string)}
                size="small"
                variant="filled"
              />
            </>
          </RoleGuard>
        </Stack>
      </Box>

      {/* Content */}
      <Typography
        sx={{
          mb: 3,
          lineHeight: 1.6,
          color: 'text.secondary',
          whiteSpace: 'pre-wrap',
        }}
        variant="body2"
      >
        {excludeLink ? (data?.message ? truncateString(data.message, 200) : 'No Message') : data?.message}
      </Typography>

      {/* File Attachments */}
      {data.files && data.files.length > 0 ? <Box sx={{ mb: 3 }}>
          <Typography color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }} variant="caption">
            Attachments ({data.files.length})
          </Typography>
          <Button
            onClick={() => { setAttachmentsOpen(true); }}
            size="small"
            startIcon={<PaperclipIcon size={14} />}
            variant="outlined"
          >
            View Attachments
          </Button>
        </Box> : null}

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
            Type
          </Typography>
          <Chip label={toTitleCase(data.type as string)} size="small" sx={{ fontWeight: 500 }} variant="outlined" />
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_OWNER,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.COMMUNITY_MEMBER]}
          >
            {(data.send_email || data.send_push || data.send_sms) ? <Box sx={{ display: 'flex', gap: 1, mb: 1, justifyContent: 'flex-end' }}>
                {data.send_email ? <Tooltip arrow placement="top" title="Email">
                    <IconButton size="small" sx={{ color: 'primary.main' }}>
                      <EmailOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip> : null}
                {data.send_push ? <Tooltip arrow placement="top" title="Push Notification">
                    <IconButton size="small" sx={{ color: 'primary.main' }}>
                      <Phone01 fontSize="small" />
                    </IconButton>
                  </Tooltip> : null}
              </Box> : null}
          </RoleGuard>

          {isScheduled ? (
            isFuture ? (
              <Box>
                <Typography color="text.secondary" sx={{ display: 'block' }} variant="caption">
                  Scheduled for
                </Typography>
                <Typography color="success.main" sx={{ fontWeight: 600 }} variant="caption">
                  {dayjs(data.scheduled_at).format('MMM DD, YYYY hh:mm A')}
                </Typography>
              </Box>
            ) : (
              data.processed_on && (
                <Box>
                  <Typography color="text.secondary" sx={{ display: 'block' }} variant="caption">
                    Delivered on
                  </Typography>
                  <Typography color="info.main" sx={{ fontWeight: 600 }} variant="caption">
                    {dayjs(data.processed_on).format('MMM DD, YYYY hh:mm A')}
                  </Typography>
                </Box>
              )
            )
          ) : (
            data.processed_on && (
              <Box>
                <Typography color="text.secondary" sx={{ display: 'block' }} variant="caption">
                  Delivered on
                </Typography>
                <Typography color="info.main" sx={{ fontWeight: 600 }} variant="caption">
                  {dayjs(data.processed_on).format('MMM DD, YYYY hh:mm A')}
                </Typography>
              </Box>
            )
          )}
        </Box>
      </Box>
    </CardContent>
  );

  return (
    <>
      <Card
        elevation={0}
        sx={{
          height: '100%',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}20`,
          },
        }}
        variant="outlined"
      >
        {excludeLink ? (
          cardContent
        ) : (
          <Link
            component={RouterLink}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              height: '100%',
              display: 'block',
              cursor: 'pointer',
            }}
            to={`${paths.announcements.index}/${data.id}`}
            underline="none"
          >
            {cardContent}
          </Link>
        )}
      </Card>
      <AnnouncementAttachmentsDialog
        files={data.files as any}
        onClose={() => { setAttachmentsOpen(false); }}
        open={attachmentsOpen}
      />
    </>
  );
};

export default AnnouncementCard;
